import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { baseUrl, forgerockErrors } from "../utilities/configurations";
import { FRAuth, StepType, TokenManager } from "@forgerock/javascript-sdk";
import { useNavigate } from "react-router-dom";
import apiRequest from "../utilities/request";
import { useAuth } from "./AuthContext";
import PropTypes from "prop-types";
import axios from "axios";
import dayjs from "dayjs";
import {
  replacePayloadCBValueBasedOnPrompt,
  replaceCBValueBasedOnPrompt,
} from "../Components/CustomFunctions";
import {
  validateMobileNumber,
  validateCardNumber,
  validateEmail,
  startJourney,
  resetConfig,
} from "../utilities/helper";
import { server_error_message, server_error_title } from "../utilities/values";

const RegisterContext = createContext();

export function useRegisterContext() {
  return useContext(RegisterContext);
}

export function RegisterProvider(props) {
  const [showTermsConditionModal, setShowTermsConditionModal] = useState(false);
  const [showPrivacyNoticeModal, setShowPrivacyNoticeModal] = useState(false);
  const [showMemberConsentModal, setShowMemberConsentModal] = useState(false);
  const [personalMaxicareNumber, setPersonalMaxicareNumber] = useState("");
  const [memberConsentChecked, setMemberConsentChecked] = useState(false);
  const [showExpiredOtpModal, setShowExpiredOtpModal] = useState(false);
  const [isMiddleNameChecked, setIsMiddleNameChecked] = useState(false);
  const [displayMemberExist, setDisplayMemberExist] = useState(false);
  const [confirmEmailAddress, setConfirmEmailAddress] = useState("");
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [configRestarted, setConfigRestarted] = useState(false);
  const [updateLoadState, setUpdateLoadState] = useState("");
  const [employeeNumber, setEmployeeNumber] = useState("");
  const [displayAlert, setDisplayAlert] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [selectedType, setSelectedType] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [journeyStep, setJourneyStep] = useState(null);
  const [updateParams, setUpdateParams] = useState({});
  const [companyCode, setCompanyCode] = useState("");
  const [isConsentID, setIsConsentID] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [alertTitle, setAlertTitle] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [suffixName, setSuffixName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [isTermsID, setIsTermsID] = useState("");
  const [lastName, setLastName] = useState("");
  const [birthDate, setBirthDate] = useState();
  const { setIsAuthenticated } = useAuth();
  const [email, setEmail] = useState("");
  const [step, setStep] = useState(1);
  const [otp, setOtp] = useState("");
  const navigate = useNavigate();

  const lettersOnly = /^[a-zA-ZñÑ\s-]+$/;
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;

  useEffect(() => {
    if (configRestarted) {
      resetConfig();
      setConfigRestarted(false);
      init();
    }
  }, [configRestarted]);

  const init = async () => {
    startJourney(process.env.REACT_APP_JOURNEY_REGISTER);
    setJourneyStep(null);
    try {
      const initialStep = await FRAuth.start();
      setJourneyStep(initialStep);
    } catch (err) {
      setAlertMessage(
        "Activate initialization was unsuccessful. Please refresh the page."
      );
    }
  };

  const resetValues = () => {
    setMemberConsentChecked(false);
    setIsMiddleNameChecked(false);
    setPersonalMaxicareNumber("");
    setDisplayMemberExist(false);
    setConfirmEmailAddress("");
    setTermsChecked(false);
    setEmployeeNumber("");
    setMobileNumber("");
    setBirthDate(null);
    setBirthDate(null);
    setCompanyCode("");
    setMiddleName("");
    setSuffixName("");
    setFirstName("");
    setLastName("");
    setEmail("");
    setOtp("");
  };

  function isPersonalValid() {
    return (
      personalMaxicareNumber !== "" &&
      firstName !== "" &&
      (isMiddleNameChecked || middleName !== "") &&
      lastName !== "" &&
      birthDate !== undefined &&
      mobileNumber !== "" &&
      email !== "" &&
      confirmEmailAddress !== ""
    );
  }

  function isCorporateValid() {
    return (
      companyCode !== "" &&
      employeeNumber !== "" &&
      firstName !== "" &&
      (isMiddleNameChecked || middleName !== "") &&
      lastName !== "" &&
      birthDate !== undefined &&
      mobileNumber !== "" &&
      email !== "" &&
      confirmEmailAddress !== ""
    );
  }

  function isEmailValid() {
    return email !== "";
  }

  function isEmailComplete() {
    return email !== "" && emailRegex.test(email);
  }

  useEffect(() => {
    if (!isPersonalValid() && !isCorporateValid()) {
      navigate("/activate/choose-membership", { replace: true });
    }
  }, []);

  const formatMobileNumber = (e) => {
    const inputVal = e.target.value.replace(/ /g, "");
    let inputNumbersOnly = inputVal.replace(/\D/g, "");

    if (inputNumbersOnly.length > 10) {
      inputNumbersOnly = inputNumbersOnly.substr(0, 10);
    }

    setMobileNumber(inputNumbersOnly);
  };

  const firstNameValidation = (e) => {
    const inputValue = e.target.value;
    const regexPattern = /^[a-zA-ZñÑ\s-]+$/;

    if (inputValue === "" || regexPattern.test(inputValue)) {
      setFirstName(inputValue);
    }
  };

  const middleNameValidation = (e) => {
    const inputValue = e.target.value;
    const regexPattern = /^[a-zA-ZñÑ\s-]+$/;

    if (inputValue === "" || regexPattern.test(inputValue)) {
      setMiddleName(inputValue);
    }
  };

  const lastNameValidation = (e) => {
    const inputValue = e.target.value;
    const regexPattern = /^[a-zA-ZñÑ\s-]+$/;

    if (inputValue === "" || regexPattern.test(inputValue)) {
      setLastName(inputValue);
    }
  };

  const companyCodeValidation = (e) => {
    const inputValue = e.target.value;
    const regexPattern = /^[a-zA-Z0-9]+$/;

    if (inputValue === "" || regexPattern.test(inputValue)) {
      setCompanyCode(inputValue);
    }
  };

  const employeeNumberValidation = (e) => {
    const inputValue = e.target.value;
    const regexPattern = /^[a-zA-Z0-9!@$%^&*_\- ]+$/;

    if (inputValue === "" || regexPattern.test(inputValue)) {
      setEmployeeNumber(inputValue);
    }
  };

  const suffixValidation = (e) => {
    const inputValue = e.target.value;
    const regexPattern = /^[IVJRSXivjrsx. ]+$/;

    if (inputValue === "" || regexPattern.test(inputValue)) {
      setSuffixName(inputValue);
    }
  };

  const formatAndsetMaxicareCardNumber = (e) => {
    const input = e.target;
    const inputValue = input.value.replace(/\s/g, "");
    const numericValue = inputValue.replace(/\D/g, "");
    const selectionStart = input.selectionStart;
    const isDeleting = e.nativeEvent.inputType === "deleteContentBackward";

    if (/^\d*$/.test(inputValue) && numericValue.length <= 16) {
      const numberWithSpaces = numericValue.replace(/(\d{4})/g, "$1 ").trim();
      setPersonalMaxicareNumber(numberWithSpaces);

      let newCaretPosition = selectionStart;

      if (
        isDeleting &&
        selectionStart > 0 &&
        numberWithSpaces[selectionStart - 1] === " "
      ) {
        newCaretPosition--;
      }

      if (
        !isDeleting &&
        selectionStart > 0 &&
        numberWithSpaces[selectionStart - 1] === " "
      ) {
        newCaretPosition++;
      }

      setTimeout(() => {
        input.setSelectionRange(newCaretPosition, newCaretPosition);
      });
    }
  };

  const handleEmail = async () => {
    const res = await axios.get("https://api.ipify.org/?format=json");
    const city = await axios.get(
      `https://ipapi.co/${JSON.stringify(res.data.ip).replaceAll(
        '"',
        ""
      )}/json/`
    );

    const emailResponse = async (response) => {
      try {
        await axios
          .post(
            baseUrl + "/otp/send-member-details-email-otp",
            {
              email: email,
              ipAddress: JSON.stringify(res.data.ip).split('"').join(""),
              location: JSON.stringify(city.data.city).split('"').join(""),
            },
            {
              headers: {
                Authorization: `Bearer ${response.data.data.accessToken}`,
              },
            }
          )
          .then((response) => {
            if (response.data.status === true) {
              setAlertTitle("");
              setAlertMessage("");
              navigate("/activate/card-delivery/verify-otp", {
                replace: true,
              });
            }
            if (response.data.status === false) {
              setAlertMessage("Please enter your own email address.");
            }
          });
      } catch (error) {
        setDisplayAlert(true);
      } finally {
        setIsLoading(false);
      }
    };

    if (isEmailValid() && isEmailComplete()) {
      try {
        const response = await axios.post(
          baseUrl + "/connect-jwt",
          {
            username: process.env.REACT_APP_CONNECT_USERNAME,
            password: process.env.REACT_APP_CONNECT_PASSWORD,
          },
          {
            headers: { "content-type": "application/json" },
          }
        );
        emailResponse(response);
      } catch (error) {
        console.log(error);
      }
    } else {
      setAlertMessage("Please enter a valid email");
    }
  };

  const addMember = async () => {
    try {
      const fr_tokens = await TokenManager.getTokens();
      if (fr_tokens) {
        await apiRequest(baseUrl + "/activate/add-member", "POST", {})
        setIsLoading(false);
        navigate("/activate/success", { replace: true });
      }
    } catch (err) {
      console.log(err);
      setIsLoading(false);
      setDisplayAlert(true);
      setAlertTitle(server_error_title);
      setAlertMessage(server_error_message);
    }
  };

  const handleVerify = async () => {
    setIsLoading(true);
    let journey_obj = JSON.parse(JSON.stringify(journeyStep));
    journey_obj.callbacks[0].payload.input[0].value = otp;
    journey_obj.payload.callbacks[0].input[0].value = otp;
    try {
      const nextStep = await FRAuth.next(journey_obj);
      switch (nextStep.type) {
        case StepType.Step:
          if (nextStep.payload.callbacks[0].type === "PasswordCallback") {
            setAlertMessage("Oops! Incorrect OTP. Please try again");
          } else if (
            nextStep.payload.callbacks[0].type === "TextOutputCallback" &&
            nextStep.payload.callbacks[0].output[0].value === "OTP is Expired"
          ) {
            setShowExpiredOtpModal(true);
          }
          setIsLoading(false);
          setJourneyStep(nextStep);
          break;
        case StepType.LoginSuccess:
          setIsAuthenticated(true);
          addMember();
          break;
        case StepType.LoginFailure:
          {
            const detail = nextStep.getDetail();
            const errorReason = nextStep.getReason();
            const errorMessage = nextStep.getMessage();
            if (detail?.errorCode === "110") {
              setDisplayAlert(true);
              setAlertTitle(errorReason);
              setAlertMessage(errorMessage);
            } else {
              setDisplayAlert(true);
              setAlertTitle("Error");
              setAlertMessage(
                forgerockErrors[detail?.failureUrl] || forgerockErrors["SWW"]
              );
            }
            resetValues();
            setConfigRestarted(true);
            setIsLoading(false);
          }
          break;
      }
    } catch (err) {
      setDisplayAlert(true);
      setAlertTitle(server_error_title);
      setAlertMessage(server_error_message);
      resetValues();
      setConfigRestarted(true);
      setIsLoading(false);
      console.log(err);
    }
  };

  const handleAcceptTerms = async (step3) => {
    let journey_obj = JSON.parse(JSON.stringify(step3));
    journey_obj.callbacks[0].payload.input[0].value = termsChecked;
    journey_obj.payload.callbacks[0].input[0].value = termsChecked;

    try {
      const nextStep = await FRAuth.next(journey_obj);
      switch (nextStep.type) {
        case StepType.Step:
          setJourneyStep(nextStep);
          navigate("/activate/verify-otp", { replace: true });
          break;
        case StepType.LoginFailure:
          {
            const detail = nextStep.getDetail();
            const errorReason = nextStep.getReason();
            const errorMessage = nextStep.getMessage();
            if (detail?.errorCode === "110") {
              setDisplayAlert(true);
              setAlertTitle(errorReason);
              setAlertMessage(errorMessage);
            } else {
              setDisplayAlert(true);
              setAlertTitle("Error");
              setAlertMessage(
                forgerockErrors[detail?.failureUrl] || forgerockErrors["SWW"]
              );
            }
            resetValues();
            setConfigRestarted(true);
          }
          break;
      }
    } catch (err) {
      setDisplayAlert(true);
      setAlertTitle(server_error_title);
      setAlertMessage(server_error_message);
      resetValues();
      setConfigRestarted(true);
      console.log(err);
    } finally{
      //loading state of activate button
      setIsLoading(false);
    }
  };

  const handleGetOtherDetails = async (step2, member) => {
    let statusCodes = {
      "02": "active",
    };
    let status = member?.memberStatus;
    if (!["active", "inactive"].includes(status?.toLowerCase())) {
      status = statusCodes[status] || "inactive";
    }
    let journey_obj = JSON.parse(JSON.stringify(step2));
    replaceCBValueBasedOnPrompt(journey_obj.callbacks, "Status", status);
    replacePayloadCBValueBasedOnPrompt(
      journey_obj.payload.callbacks,
      "Status",
      status
    );
    replaceCBValueBasedOnPrompt(
      journey_obj.callbacks,
      "Member Type",
      member?.memberType
    );
    replacePayloadCBValueBasedOnPrompt(
      journey_obj.payload.callbacks,
      "Member Type",
      member?.memberType
    );
    replaceCBValueBasedOnPrompt(
      journey_obj.callbacks,
      "Gender",
      member?.gender
    );
    replacePayloadCBValueBasedOnPrompt(
      journey_obj.payload.callbacks,
      "Gender",
      member?.gender
    );
    replaceCBValueBasedOnPrompt(
      journey_obj.callbacks,
      "SuffixName",
      member?.suffixName
    );
    replacePayloadCBValueBasedOnPrompt(
      journey_obj.payload.callbacks,
      "SuffixName",
      member?.suffixName
    );
    replaceCBValueBasedOnPrompt(
      journey_obj.callbacks,
      "Role",
      status.toLowerCase() === "active" ? "Member" : "Guest"
    );
    replacePayloadCBValueBasedOnPrompt(
      journey_obj.payload.callbacks,
      "Role",
      status.toLowerCase() === "active" ? "Member" : "Guest"
    );
    replaceCBValueBasedOnPrompt(
      journey_obj.callbacks,
      "Corp Code",
      member?.corpCode || "INDIVIDUAL"
    );
    replacePayloadCBValueBasedOnPrompt(
      journey_obj.payload.callbacks,
      "Corp Code",
      member?.corpCode || "INDIVIDUAL"
    );
    replaceCBValueBasedOnPrompt(
      journey_obj.callbacks,
      "Policy Number",
      member?.policyNumber
    );
    replacePayloadCBValueBasedOnPrompt(
      journey_obj.payload.callbacks,
      "Policy Number",
      member?.policyNumber
    );
    replaceCBValueBasedOnPrompt(
      journey_obj.callbacks,
      "PersonID",
      member?.personId
    );
    replacePayloadCBValueBasedOnPrompt(
      journey_obj.payload.callbacks,
      "PersonID",
      member?.personId
    );

    try {
      const nextStep = await FRAuth.next(journey_obj);
      switch (nextStep.type) {
        case StepType.Step:
          setJourneyStep(nextStep);
          handleAcceptTerms(nextStep);
          break;
        case StepType.LoginFailure:
          {
            const detail = nextStep.getDetail();
            const errorReason = nextStep.getReason();
            const errorMessage = nextStep.getMessage();
            if (detail?.errorCode === "110") {
              setDisplayAlert(true);
              setAlertTitle(errorReason);
              setAlertMessage(errorMessage);
            } else {
              setDisplayAlert(true);
              setAlertTitle("Error");
              setAlertMessage(
                forgerockErrors[detail?.failureUrl] || forgerockErrors["SWW"]
              );
            }
            resetValues();
            setConfigRestarted(true);
            setIsLoading(false);
          }
          break;
      }
    } catch (err) {
      setIsLoading(false);
      setDisplayAlert(true);
      setAlertTitle(server_error_title);
      setAlertMessage(server_error_message);
      resetValues();
      setConfigRestarted(true);
      console.log(err);
    }
  };

  const handleNext = (e) => {
    try {
      e.preventDefault();
    } catch (error) {
      console.log(error);
    }

    const connectAPI = async () => {
      await axios
        .post(`${baseUrl}/connect`, {
          username: process.env.REACT_APP_CONNECT_USERNAME,
          password: process.env.REACT_APP_CONNECT_PASSWORD,
        })
        .then((res) => {
          if (res.data.status === true) {
            getMemberInfo();
          } else {
            setIsLoading(false);
            setDisplayAlert(true);
            setAlertTitle("Error");
            setAlertMessage("Something went wrong");
          }
        })
        .catch((err) => {
          console.log(err);
          setIsLoading(false);
          setDisplayAlert(true);
          setAlertTitle(server_error_title);
          setAlertMessage(server_error_message);
        });
    };

    const getMemberInfo = async () => {
      const params = {
        cardNo: personalMaxicareNumber.replaceAll(" ", "") || "",
        corpCode: companyCode,
        empNo: employeeNumber,
        type: selectedType,
        firstName: firstName,
        middleName: middleName,
        lastName: lastName,
        suffixName: suffixName,
        birthDate: dayjs(birthDate).format("MM/DD/YYYY"),
        mobileNo: mobileNumber,
      }
      await axios
        .post(`${baseUrl}/activate/verify-member`, params)
        .then((res) => {
          if (res.data.status) {
            getStep(res.data.data);
          } else {
            if (
              res.data.message === "Mismatch." ||
              res.data.message === "Not Verified." ||
              res.data.message === "Member not found." ||
              res.data.message === "Member is not active."
            ) {
              setDisplayAlert(true);
              setAlertTitle("Oops! No enrolled member found");
              setAlertMessage(
                "We couldn't find any member enrollment with the provided details. Let's double-check and try again."
              );
            } else if (res.data.message === "Member account already exists.") {
              setDisplayAlert(true);
              setAlertTitle("Uh-Oh! Account Already Exists");
              setAlertMessage(
                "Oops! It looks like there's already an account with the provided information. Please double-check the details or try logging in if you already have an account."
              );
            } else if (res.data.message === "Member account already exists but is inactive.") {
              setShowUpdateModal(true);
              setUpdateParams(params);
            } else {
              setDisplayAlert(true);
              setAlertTitle("Activation Error");
              setAlertMessage(res.data.message);
            }
            setIsLoading(false);
          }
        })
        .catch((err) => {
          console.log(err);
          setIsLoading(false);
          setDisplayAlert(true);
          setAlertTitle(server_error_title);
          setAlertMessage(server_error_message);
        });
    };

    const getStep = async (member) => {
      let journey_obj = JSON.parse(JSON.stringify(journeyStep));
      replaceCBValueBasedOnPrompt(
        journey_obj.callbacks,
        "Maxicare Card Number",
        parseInt(member?.cardNo) || null
      );
      replacePayloadCBValueBasedOnPrompt(
        journey_obj.payload.callbacks,
        "Maxicare Card Number",
        parseInt(member?.cardNo) || null
      );
      replaceCBValueBasedOnPrompt(
        journey_obj.callbacks,
        "Corp Code",
        member?.corpCode || "INDIVIDUAL"
      );
      replacePayloadCBValueBasedOnPrompt(
        journey_obj.payload.callbacks,
        "Corp Code",
        member?.corpCode || "INDIVIDUAL"
      );
      replaceCBValueBasedOnPrompt(
        journey_obj.callbacks,
        "Employee Id",
        member?.empNo || "INDIVIDUAL"
      );
      replacePayloadCBValueBasedOnPrompt(
        journey_obj.payload.callbacks,
        "Employee Id",
        member?.empNo || "INDIVIDUAL"
      );
      replaceCBValueBasedOnPrompt(
        journey_obj.callbacks,
        "First Name",
        firstName
      );
      replacePayloadCBValueBasedOnPrompt(
        journey_obj.payload.callbacks,
        "First Name",
        firstName
      );
      replaceCBValueBasedOnPrompt(
        journey_obj.callbacks,
        "Middle Name",
        middleName
      );
      replacePayloadCBValueBasedOnPrompt(
        journey_obj.payload.callbacks,
        "Middle Name",
        middleName
      );
      replaceCBValueBasedOnPrompt(journey_obj.callbacks, "Last Name", lastName);
      replacePayloadCBValueBasedOnPrompt(
        journey_obj.payload.callbacks,
        "Last Name",
        lastName
      );
      replaceCBValueBasedOnPrompt(
        journey_obj.callbacks,
        "Date of Birth",
        dayjs(birthDate).format("YYYY-MM-DD")
      );
      replacePayloadCBValueBasedOnPrompt(
        journey_obj.payload.callbacks,
        "Date of Birth",
        dayjs(birthDate).format("YYYY-MM-DD")
      );
      replaceCBValueBasedOnPrompt(
        journey_obj.callbacks,
        "Telephone Number",
        `+63${mobileNumber}`
      );
      replacePayloadCBValueBasedOnPrompt(
        journey_obj.payload.callbacks,
        "Telephone Number",
        `+63${mobileNumber}`
      );
      replaceCBValueBasedOnPrompt(
        journey_obj.callbacks,
        "Email Address",
        email
      );
      replacePayloadCBValueBasedOnPrompt(
        journey_obj.payload.callbacks,
        "Email Address",
        email
      );

      try {
        const nextStep = await FRAuth.next(journey_obj);
        switch (nextStep.type) {
          case StepType.Step:
            setJourneyStep(nextStep);
            handleGetOtherDetails(nextStep, member);
            break;
          case StepType.LoginFailure:
            {
              const detail = nextStep.getDetail();
              const errorReason = nextStep.getReason();
              const errorMessage = nextStep.getMessage();
              if (detail?.errorCode === "110") {
                setDisplayAlert(true);
                setAlertTitle(errorReason);
                setAlertMessage(errorMessage);
              } else {
                setDisplayAlert(true);
                setAlertTitle("Error");
                setAlertMessage(
                  forgerockErrors[detail?.failureUrl] || forgerockErrors["SWW"]
                );
              }
              setConfigRestarted(true);
              setIsLoading(false);
            }
            break;
        }
      } catch (err) {
        setIsLoading(false);
        setDisplayAlert(true);
        setAlertTitle(server_error_title);
        setAlertMessage(server_error_message);
        setConfigRestarted(true);
        console.log(err);
      }
    };

    if (selectedType === "Personal" && isPersonalValid()) {
      if (!validateCardNumber(personalMaxicareNumber)) {
        setDisplayAlert(true);
        setAlertTitle("Oops! Invalid card number");
        setAlertMessage("Please enter a valid card number.");
      } else if (!validateMobileNumber(`+63${mobileNumber}`)) {
        setDisplayAlert(true);
        setAlertTitle("Oops! Invalid mobile number");
        setAlertMessage("Please enter a valid mobile number.");
      } else if (!validateEmail(email)) {
        setDisplayAlert(true);
        setAlertTitle("Oops! Invalid email address");
        setAlertMessage("Please enter a valid email address.");
      } else if (email !== confirmEmailAddress) {
        setDisplayAlert(true);
        setAlertTitle("Email address mismatch");
        setAlertMessage("The email addresses do not match.");
      } else {
        setIsLoading(true);
        connectAPI();
      }
    } else if (selectedType === "Corporate" && isCorporateValid()) {
      if (!validateMobileNumber(`+63${mobileNumber}`)) {
        setDisplayAlert(true);
        setAlertTitle("Oops! Invalid mobile number");
        setAlertMessage("Please enter a valid mobile number.");
      } else if (!validateEmail(email)) {
        setDisplayAlert(true);
        setAlertTitle("Oops! Invalid email address");
        setAlertMessage("Please enter a valid email address.");
      } else if (email !== confirmEmailAddress) {
        setDisplayAlert(true);
        setAlertTitle("Email address mismatch");
        setAlertMessage("The email addresses do not match.");
      } else {
        setIsLoading(true);
        connectAPI();
      }
    }
  };

  const handleUpdateDetails=async()=>{
    try {
      setUpdateLoadState("update");
      const res = await axios.post(`${baseUrl}/activate/update-member`, updateParams);
      console.log(res);
      setUpdateLoadState("success");
    } catch (error) {
      setUpdateLoadState("failed");
    }
  }

  const handleBack = () => {
    navigate(-1);
    resetConfig();
  };

  const data = useMemo(
    () => ({
      formatAndsetMaxicareCardNumber,
      setShowTermsConditionModal,
      setShowPrivacyNoticeModal,
      setShowMemberConsentModal,
      setPersonalMaxicareNumber,
      employeeNumberValidation,
      setMemberConsentChecked,
      setConfirmEmailAddress,
      setIsMiddleNameChecked,
      setShowExpiredOtpModal,
      setDisplayMemberExist,
      companyCodeValidation,
      middleNameValidation,
      firstNameValidation,
      handleUpdateDetails,
      formatMobileNumber,
      setShowUpdateModal,
      setUpdateLoadState,
      lastNameValidation,
      setConfigRestarted,
      setEmployeeNumber,
      suffixValidation,
      isCorporateValid,
      isPersonalValid,
      isEmailComplete,
      setMobileNumber,
      setDisplayAlert,
      setAlertMessage,
      setSelectedType,
      setTermsChecked,
      setIsConsentID,
      setCompanyCode,
      setJourneyStep,
      setSuffixName,
      setMiddleName,
      setAlertTitle,
      handleVerify,
      setBirthDate,
      isEmailValid,
      setFirstName,
      setIsLoading,
      setIsTermsID,
      resetValues,
      setLastName,
      handleEmail,
      handleBack,
      handleNext,
      setEmail,
      navigate,
      setStep,
      setOtp,
      init,

      showTermsConditionModal,
      showMemberConsentModal,
      showPrivacyNoticeModal,
      personalMaxicareNumber,
      memberConsentChecked,
      showExpiredOtpModal,
      confirmEmailAddress,
      isMiddleNameChecked,
      displayMemberExist,
      updateLoadState,
      showUpdateModal,
      configRestarted,
      employeeNumber,
      alertMessage,
      termsChecked,
      displayAlert,
      mobileNumber,
      selectedType,
      journeyStep,
      companyCode,
      lettersOnly,
      isConsentID,
      emailRegex,
      middleName,
      suffixName,
      alertTitle,
      firstName,
      birthDate,
      isLoading,
      isTermsID,
      lastName,
      email,
      step,
      otp,
    }),
    [
      isCorporateValid,
      isPersonalValid,
      isEmailComplete,
      isEmailValid,

      showTermsConditionModal,
      showMemberConsentModal,
      showPrivacyNoticeModal,
      personalMaxicareNumber,
      memberConsentChecked,
      showExpiredOtpModal,
      confirmEmailAddress,
      isMiddleNameChecked,
      displayMemberExist,
      updateLoadState,
      showUpdateModal,
      configRestarted,
      employeeNumber,
      alertMessage,
      termsChecked,
      displayAlert,
      mobileNumber,
      selectedType,
      journeyStep,
      companyCode,
      lettersOnly,
      isConsentID,
      emailRegex,
      middleName,
      suffixName,
      alertTitle,
      firstName,
      birthDate,
      isLoading,
      isTermsID,
      lastName,
      email,
      step,
      otp,
    ]
  );

  return (
    <RegisterContext.Provider value={data}>
      {props.children}
    </RegisterContext.Provider>
  );
}

RegisterProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
